import React from 'react';
import { Img } from 'react-image';
import { imageCache } from '../utils/imageCache';

interface MonsterImageProps {
  imgPath: string | null;
  version?: number;
  alt: string;
  style?: React.CSSProperties;
  className?: string;
  onClick?: () => void;
  onLoad?: () => void;
  onError?: () => void;
  /**
   * 콜라보 짝의 이미지. 주면 왼쪽 절반은 이 몹, 오른쪽 절반은 짝으로 그린다.
   *
   * **지금은 아무도 넘기지 않는다.** 반반 그림은 서버가 만들어 같은 주소로 내보낸다
   * (backend utils/collabImage.ts) — 몹을 그리는 자리가 화면마다 60곳이 넘어서, 거기마다
   * 두 장을 겹치면 반드시 빠뜨리는 곳이 생겼다. 이 길은 특별한 경우를 위해 남겨 둔다.
   */
  collabImgPath?: string | null;
}

const MonsterImage: React.FC<MonsterImageProps> = ({
  imgPath,
  version,
  alt,
  style,
  className,
  onClick,
  onLoad,
  onError,
  collabImgPath
}) => {
  // URL 계산 - version이 명시적으로 전달된 경우에만 사용 (관리 팝업에서만)
  const imageUrl = !imgPath ? '/images/empty.png' : imageCache.buildImageUrl(imgPath, version);

  /** 짝의 그림이 없을 때. 그때는 반반을 포기하고 한 장으로 그린다 */
  const [collabFailed, setCollabFailed] = React.useState(false);
  React.useEffect(() => { setCollabFailed(false); }, [collabImgPath]);

  /**
   * 콜라보 짝이 있으면 한 칸에 반반으로 그린다.
   *
   * **`background-image: url(...)` 로 하면 안 된다.** 몹 파일명에는 공백과 괄호가
   * 있어서(`Forest Keeper.png`, `알타이어(빛).png`) 따옴표 없는 `url()` 이 깨진다 —
   * 실제로 오른쪽 절반이 안 나왔다. `<img loading="lazy">` 로 그리면 브라우저가 주소를 알아서
   * 처리하고, 파일이 없을 때 `onError` 로 되돌릴 수도 있다.
   */
  if (collabImgPath && !collabFailed) {
    const collabUrl = imageCache.buildImageUrl(collabImgPath, version);
    const half = (side: 'left' | 'right'): React.CSSProperties => ({
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      // 가운데를 **세로로 정확히 반** 자른다. 원본 파일은 그대로 두고 보이는 부분만 자른다.
      // 예전에는 비스듬히 잘랐는데 위아래에서 보이는 몫이 달라 헷갈렸다(서버 쪽도 같이 바꿨다).
      clipPath: side === 'left'
        ? 'polygon(0 0, 50% 0, 50% 100%, 0 100%)'
        : 'polygon(50% 0, 100% 0, 100% 100%, 50% 100%)'
    });
    return (
      <div
        className={className}
        onClick={onClick}
        title={alt}
        style={{ ...style, position: 'relative', overflow: 'hidden' }}
      >
        <img loading="lazy" src={imageUrl} alt={alt} style={half('left')} onLoad={onLoad} />
        <img loading="lazy"
          src={collabUrl}
          alt={alt}
          style={half('right')}
          // 짝의 그림이 없으면 반반을 포기하고 원래 한 장으로 돌아간다
          onError={() => setCollabFailed(true)}
        />
        {/* 경계선 — 두 몹이 겹쳐 있다는 걸 알아볼 수 있게 (가운데 세로) */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(90deg, transparent 49.4%, rgba(102,126,234,0.55) 49.4%, rgba(102,126,234,0.55) 50.6%, transparent 50.6%)'
        }} />
      </div>
    );
  }

  return (
    <Img
      src={imageUrl}
      unloader={
        <img loading="lazy"
          src="/images/empty.png"
          alt={alt}
          style={style}
          className={className}
          onClick={onClick}
        />
      }
      alt={alt}
      style={style}
      className={className}
      onClick={onClick}
      onLoad={onLoad}
      onError={onError}
    />
  );
};

export default MonsterImage; 